import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    getNCoreUIKitVersion,
    NCoreUIKitTheme,
    RowCard,
    Avatar,
    Switch,
    Chip
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    InlineCode,
    DocsTable,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    ChevronRightIcon,
    StarIcon,
    UserIcon,
    InfoIcon,
    BellIcon,
    HomeIcon,
    FileIcon
} from "../../anatomy/icons";

const NAME = "RowCard";

const USAGE_CODE = `<View
    style={listStyle}
>
    <RowCard
        subTitle="ada@example.com"
        rightIcon={ChevronRightIcon}
        title="Email address"
        onPress={handleOpenEmail}
        icon={UserIcon}
        isInList={true}
    />
    <RowCard
        title="App version"
        rightTitle="2.0.0"
        isInList={true}
    />
</View>`;

const SWITCH_CODE = `<RowCard
    renderTrailing={() => <View
        aria-hidden={true}
    >
        <Switch
            accessibilityLabel="Push notifications"
            isChecked={isPushEnabled}
            spreadBehaviour="free"
            focusable={false}
        />
    </View>}
    onPress={() => setIsPushEnabled(!isPushEnabled)}
    aria-checked={isPushEnabled}
    title="Push notifications"
    icon={BellIcon}
    isInList={true}
    role="switch"
/>`;

const VERSION = getNCoreUIKitVersion();

const LAYOUT_TYPES = [
    "first-based",
    "evenly"
] as const;

const COLUMN_STYLE: CSSProperties = {
    alignItems: "flex-start",
    flexDirection: "column",
    display: "flex"
};

const GRID_STYLE: CSSProperties = {
    justifyContent: "center",
    alignItems: "flex-start",
    flexWrap: "wrap",
    display: "flex"
};

const FRAME_STYLE = {
    width: "100%"
};

const ListFrame = ({
    isNarrow = false,
    children
}: {
    children: ReactNode;
    isNarrow?: boolean;
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <div
        style={{
            ...FRAME_STYLE,
            maxWidth: isNarrow ? shapeConfigs.drawerWidth : undefined
        }}
    >
        <DemoFrame>
            <View
                style={{
                    padding: spaces.spacingXs
                }}
            >
                {children}
            </View>
        </DemoFrame>
    </div>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("rowCardPage.slotsTitle"),
        id: "slots"
    },
    {
        title: t("rowCardPage.layoutTitle"),
        id: "layout"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("rowCardPage.appearanceTitle"),
        id: "appearance"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const RowCardPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        radiuses,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        selectedKey,
        setSelectedKey
    ] = useState("profile");

    const [
        isPushEnabled,
        setIsPushEnabled
    ] = useState(true);

    const [
        pressCount,
        setPressCount
    ] = useState(0);

    const selectedTitle = selectedKey === "profile" ? t("rowCardPage.profile") : t("rowCardPage.favorites");

    const handleProfile = () => setSelectedKey("profile");

    const handleFavorites = () => setSelectedKey("favorites");

    const handleTogglePush = () => setIsPushEnabled(!isPushEnabled);

    const handleCount = () => setPressCount((current) => current + 1);

    const handleNothing = () => undefined;

    const renderSwitch = (isChecked: boolean, label: string) => <View
        aria-hidden={true}
    >
        <Switch
            accessibilityLabel={label}
            spreadBehaviour="free"
            isChecked={isChecked}
            focusable={false}
        />
    </View>;

    const renderLayoutRow = (type: typeof LAYOUT_TYPES[number]): ReactNode => <div
        style={{
            ...COLUMN_STYLE,
            gap: spaces.spacingMd
        }}
        key={type}
    >
        <InlineCode>
            {`type="${type}"`}
        </InlineCode>
        <ListFrame
            isNarrow={true}
        >
            <RowCard
                rightSubTitle={t("rowCardPage.layoutPages")}
                rightTitle={t("rowCardPage.layoutDraft")}
                subTitle={t("rowCardPage.layoutUpdated")}
                title={t("rowCardPage.layoutReport")}
                onPress={handleNothing}
                titleNumberOfLines={1}
                isInList={true}
                type={type}
            />
        </ListFrame>
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("rowCardPage.lead")}
            name={NAME}
        />
        <Callout
            title={t("rowCardPage.noticeTitle")}
        >
            {t("rowCardPage.noticeText")}
        </Callout>
        <DocsTable
            rows={[
                [
                    <InlineCode>
                        {"icon, renderLeading"}
                    </InlineCode>,
                    <InlineCode>
                        {"Item.Media"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"title"}
                    </InlineCode>,
                    <InlineCode>
                        {"Item.Title"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"subTitle"}
                    </InlineCode>,
                    <InlineCode>
                        {"Item.Description"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"rightTitle, rightSubTitle, rightIcon, renderTrailing"}
                    </InlineCode>,
                    <InlineCode>
                        {"Item.Actions"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"isInList"}
                    </InlineCode>,
                    <InlineCode>
                        {"Item.Group"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"backgroundColor, isTransparentBackground"}
                    </InlineCode>,
                    <InlineCode>
                        {"variant=\"plain\" | \"outline\" | \"filled\""}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"isSelected, isDisabled, isLoading, onPress"}
                    </InlineCode>,
                    t("rowCardPage.mapSameProps")
                ]
            ]}
            headers={[
                t("rowCardPage.mapOld"),
                t("rowCardPage.mapNew")
            ]}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("rowCardPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("rowCardPage.overviewNote")}
            </Paragraph>
            <Preview
                caption={t("rowCardPage.overviewCaption", [
                    selectedTitle
                ])}
                isStacked={true}
            >
                <ListFrame>
                    <RowCard
                        isSelected={selectedKey === "profile"}
                        title={t("rowCardPage.profile")}
                        rightIcon={ChevronRightIcon}
                        subTitle={t("sample.name")}
                        onPress={handleProfile}
                        icon={UserIcon}
                        isInList={true}
                    />
                    <RowCard
                        renderTrailing={() => renderSwitch(isPushEnabled, t("rowCardPage.notifications"))}
                        title={t("rowCardPage.notifications")}
                        aria-checked={isPushEnabled}
                        onPress={handleTogglePush}
                        icon={BellIcon}
                        isInList={true}
                        role="switch"
                    />
                    <RowCard
                        isSelected={selectedKey === "favorites"}
                        title={t("rowCardPage.favorites")}
                        rightIcon={ChevronRightIcon}
                        onPress={handleFavorites}
                        rightTitle="12"
                        icon={StarIcon}
                        isInList={true}
                    />
                    <RowCard
                        title={t("rowCardPage.about")}
                        rightTitle={VERSION}
                        icon={InfoIcon}
                        isInList={true}
                    />
                </ListFrame>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("rowCardPage.slotsTitle")}
            id="slots"
        >
            <Paragraph>
                {t("rowCardPage.slotsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <ListFrame>
                    <RowCard
                        title={t("rowCardPage.profile")}
                        onPress={handleNothing}
                        icon={UserIcon}
                        isInList={true}
                    />
                    <RowCard
                        subTitle={t("rowCardPage.emailValue")}
                        title={t("rowCardPage.email")}
                        onPress={handleNothing}
                        icon={BellIcon}
                        isInList={true}
                    />
                    <RowCard
                        rightSubTitle={t("sample.metricChange")}
                        subTitle={t("rowCardPage.invoiceNote")}
                        rightTitle={t("sample.amount1")}
                        title={t("rowCardPage.invoice")}
                        onPress={handleNothing}
                        icon={FileIcon}
                        isInList={true}
                    />
                    <RowCard
                        subTitle={t("rowCardPage.addressValue")}
                        title={t("rowCardPage.address")}
                        rightIcon={ChevronRightIcon}
                        onPress={handleNothing}
                        icon={HomeIcon}
                        isInList={true}
                    />
                    <RowCard
                        renderTrailing={() => <Chip
                            title={t("rowCardPage.admin")}
                            isClosable={false}
                            type="success"
                            size="small"
                        />}
                        subTitle={t("rowCardPage.online")}
                        renderLeading={() => <Avatar
                            title={t("sample.name")}
                            size="xSmall"
                        />}
                        rightIcon={ChevronRightIcon}
                        title={t("sample.name")}
                        onPress={handleNothing}
                        isInList={true}
                    />
                </ListFrame>
            </Preview>
            <CodeBlock
                code={SWITCH_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("rowCardPage.layoutTitle")}
            id="layout"
        >
            <Paragraph>
                {t("rowCardPage.layoutText")}
            </Paragraph>
            <Preview>
                <div
                    style={{
                        ...GRID_STYLE,
                        gap: spaces.spacingLg
                    }}
                >
                    {LAYOUT_TYPES.map(renderLayoutRow)}
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("rowCardPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("rowCardPage.statesCaption", [
                    pressCount
                ])}
                isStacked={true}
            >
                <ListFrame>
                    <RowCard
                        title={t("rowCardPage.stateDefault")}
                        subTitle={t("sample.subtitle")}
                        onPress={handleCount}
                        icon={UserIcon}
                        isInList={true}
                    />
                    <RowCard
                        title={t("rowCardPage.stateSelected")}
                        subTitle={t("sample.subtitle")}
                        onPress={handleNothing}
                        isSelected={true}
                        icon={UserIcon}
                        isInList={true}
                    />
                    <RowCard
                        title={t("rowCardPage.stateDisabled")}
                        subTitle={t("sample.subtitle")}
                        onPress={handleNothing}
                        isDisabled={true}
                        icon={UserIcon}
                        isInList={true}
                    />
                    <RowCard
                        title={t("rowCardPage.stateLoading")}
                        subTitle={t("sample.subtitle")}
                        rightIcon={ChevronRightIcon}
                        onPress={handleNothing}
                        isLoading={true}
                        icon={UserIcon}
                        isInList={true}
                    />
                </ListFrame>
            </Preview>
        </Section>
        <Section
            title={t("rowCardPage.appearanceTitle")}
            id="appearance"
        >
            <Paragraph>
                {t("rowCardPage.appearanceText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <ListFrame>
                    <RowCard
                        title={t("rowCardPage.signOut")}
                        onPress={handleNothing}
                        titleColor="danger"
                        iconColor="danger"
                        icon={UserIcon}
                        isInList={true}
                    />
                </ListFrame>
                <RowCard
                    subTitle={t("rowCardPage.standaloneNote")}
                    title={t("rowCardPage.standalone")}
                    borderRadius={radiuses.surface}
                    rightIcon={ChevronRightIcon}
                    backgroundColor="default"
                    onPress={handleNothing}
                    icon={FileIcon}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("rowCardPage.a11yRole"),
                    t("rowCardPage.a11yName"),
                    t("rowCardPage.a11yKeyboard"),
                    t("rowCardPage.a11ySelected"),
                    t("rowCardPage.a11yState"),
                    t("rowCardPage.a11ySwitch")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("rowCardPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("rowCardPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("rowCardPage.propRightTitle"),
                        name: "rightTitle",
                        type: "string"
                    },
                    {
                        description: t("rowCardPage.propRightSubTitle"),
                        name: "rightSubTitle",
                        type: "string"
                    },
                    {
                        description: t("rowCardPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("rowCardPage.propIconSize"),
                        name: "iconSize",
                        type: "number"
                    },
                    {
                        description: t("rowCardPage.propRightIcon"),
                        type: "NCoreUIKitIcon",
                        name: "rightIcon"
                    },
                    {
                        type: "(state: RowCardSlotStateType) => ReactNode",
                        description: t("rowCardPage.propRenderLeading"),
                        name: "renderLeading"
                    },
                    {
                        type: "(state: RowCardSlotStateType) => ReactNode",
                        description: t("rowCardPage.propRenderTrailing"),
                        name: "renderTrailing"
                    },
                    {
                        description: t("rowCardPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("rowCardPage.propOnLongPress"),
                        type: "(event) => void",
                        name: "onLongPress"
                    },
                    {
                        description: t("rowCardPage.propIsSelected"),
                        defaultValue: "false",
                        name: "isSelected",
                        type: "boolean"
                    },
                    {
                        description: t("rowCardPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("rowCardPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("rowCardPage.propIsInList"),
                        defaultValue: "false",
                        name: "isInList",
                        type: "boolean"
                    },
                    {
                        description: t("rowCardPage.propType"),
                        type: "\"first-based\" | \"evenly\"",
                        defaultValue: "\"first-based\"",
                        name: "type"
                    },
                    {
                        description: t("rowCardPage.propTitleNumberOfLines"),
                        name: "titleNumberOfLines",
                        type: "number"
                    },
                    {
                        description: t("rowCardPage.propSubTitleNumberOfLines"),
                        name: "subTitleNumberOfLines",
                        type: "number"
                    },
                    {
                        description: t("rowCardPage.propTitleVariant"),
                        defaultValue: "\"labelMediumSize\"",
                        type: "keyof Typography",
                        name: "titleVariant"
                    },
                    {
                        description: t("rowCardPage.propSubTitleVariant"),
                        defaultValue: "\"bodyMediumSize\"",
                        type: "keyof Typography",
                        name: "subTitleVariant"
                    },
                    {
                        description: t("rowCardPage.propRightTitleVariant"),
                        defaultValue: "\"labelMediumSize\"",
                        name: "rightTitleVariant",
                        type: "keyof Typography"
                    },
                    {
                        description: t("rowCardPage.propRightSubTitleVariant"),
                        defaultValue: "\"bodySmallSize\"",
                        name: "rightSubTitleVariant",
                        type: "keyof Typography"
                    },
                    {
                        description: t("rowCardPage.propTitleColor"),
                        type: "keyof TextContentColors",
                        name: "titleColor"
                    },
                    {
                        description: t("rowCardPage.propSubTitleColor"),
                        type: "keyof TextContentColors",
                        name: "subTitleColor"
                    },
                    {
                        description: t("rowCardPage.propRightTitleColor"),
                        type: "keyof TextContentColors",
                        name: "rightTitleColor"
                    },
                    {
                        description: t("rowCardPage.propRightSubTitleColor"),
                        type: "keyof TextContentColors",
                        name: "rightSubTitleColor"
                    },
                    {
                        description: t("rowCardPage.propIconColor"),
                        type: "keyof IconContentColors",
                        name: "iconColor"
                    },
                    {
                        description: t("rowCardPage.propBackgroundColor"),
                        type: "keyof ContainerContentColors",
                        name: "backgroundColor"
                    },
                    {
                        description: t("rowCardPage.propIsTransparentBackground"),
                        name: "isTransparentBackground",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("rowCardPage.propBorderRadius"),
                        name: "borderRadius",
                        type: "number"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("rowCardPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("rowCardPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default RowCardPage;
